<template>
  <div id="app">
    <the-header v-if="hasToShowTheHeader"/>
    <!-- Page title, help and breadcrumbs-->
    <the-page-header v-if="showPageHeader"/>
    <section class="app-service row hide-on-print">
      <div class="col ml-lg-5">
        <b-alert
          :show="dismissCountDown"
          @dismissed="dismissCountDown=0"
          @dismiss-count-down="countDownChanged"
          class="mt-2"
          variant="success"
          dismissible
        >
          {{ alertText }}
          <b-progress
            variant="success"
            :max="5"
            :value="5 - dismissCountDown"
            height="4px"
          ></b-progress>
        </b-alert>
        <b-alert v-model="showErrorAlert" class="mt-2" variant="danger" dismissible>
          {{ $t('Error!') }} {{ errorText }}
        </b-alert>
      </div>
    </section>
    <main class="app-main"
          :class="{overlay: $store.state.overlays.main, 'white-container': $store.state.useContentContainer}">
      <router-view v-if="!loading"></router-view>
    </main>
    <the-footer></the-footer>
    <!-- To render comments window at the end of the page-->
    <portal-target class="comments-portal" name="comments-lesson"/>
    <portal-target class="comments-portal" name="comments-unit"/>
    <portal-target class="comments-portal" name="comments-curriculum"/>
    <portal-target class="comments-report" name="comments-report"/>
    <portal-target class="comments-left" name="comments-left"/>
    <portal-target class="comments-right" name="comments-right"/>

    <!-- Notifications (Toast-messages) -->
    <app-notification v-if="!loading"/>
  </div>
</template>
<script>
import TheFooter from './components/TheFooter'
import TheHeader from './components/TheHeader'
import AppNotification from './components/htmlHelpers/AppNotification'
import ThePageHeader from './components/ThePageHeader'
import EventBus from './eventBus/eventBus'
import { mapState, mapGetters } from 'vuex'
import Vue from 'vue'
if (process.env.VUE_APP_WORKING_ENV === 'local') {
  require('./utils/injectDebugBar')
}
export default {
  name: 'App',
  components: { TheFooter, TheHeader, AppNotification, ThePageHeader },
  data: function () {
    return {
      loading: true,
      dismissCountDown: 0,
      alertText: null,
      errorText: null,
      showErrorAlert: false
    }
  },
  async created() {
    try {
      const result = await Vue.prototype.$http.get('site-labels')
      if (result.status > 399) {
        console.log('localization status : ', result.status)
        return
      }
      Vue.prototype.$siteLabels = { ...result.data }
      EventBus.$emit('site-labels-ready')
    } finally {
      this.loading = false
    }
  },
  async mounted() {
    EventBus.$on('show-alert', this.showAlert)
    EventBus.$on('show-error', this.showError)
    EventBus.$on('site-labels-ready', this.siteLabelsReady)
  },
  beforeDestroy() {
    EventBus.$off('show-alert', this.showAlert)
    EventBus.$off('show-error', this.showError)
    EventBus.$off('site-labels-ready', this.siteLabelsReady)
  },
  computed: {
    ...mapState('InfoStore', ['info', 'isTheHeaderShown']),
    ...mapGetters('InfoStore', ['isPublicUser', 'publicSiteSettings']),
    ...mapGetters('PageHeaderStore', ['isGlobalPageHeaderHidden']),
    hasToShowTheHeader() {
      if (!this.isTheHeaderShown) {
        if (this.publicSiteSettings && this.publicSiteSettings.publicSitePasswordEnabled) {
          return this.publicSiteSettings.publicSiteUserAuthorized
        }
      }
      return true
    },
    showPageHeader() {
      if (this.isPublicUser && !this.info.isPublicSiteEnabled) {
        return false
      }
      return !this.isGlobalPageHeaderHidden
    }
  },
  methods: {
    countDownChanged(dismissCountDown) {
      this.dismissCountDown = dismissCountDown
    },
    showAlert(alertText) {
      this.dismissCountDown = 5
      this.alertText = alertText
    },
    showError(errorText) {
      this.errorText = errorText
      this.showErrorAlert = true
    },
    siteLabelsReady() {
      if (this.$siteLabels) {
        const themedStyles = []
        for (const key of Object.keys(this.$siteLabels)) {
          if (key.includes('SCHEME_')) {
            themedStyles.push('--' + key.toLowerCase().replace(/_/g, '-') + ':' + this.$siteLabels[key] + ';')
          }
        }
        if (themedStyles.length) {
          const style = document.createElement('style')
          style.setAttribute('type', 'text/css')
          style.innerText = '* { ' + themedStyles.join(' ') + '}'
          document.head.appendChild(style)
        }
      }
    }
  }
}
</script>

<style lang="scss">
@import 'assets/scss/general.scss';
</style>

<style lang="scss">
@import '@/assets/css/fonts/Awesome/font-awesome.css';
@import './assets/css/stylesheet.css';
//TODO: remove imported style and scss file from assets/css ?
//@import '../node_modules/vue-multiselect/dist/vue-multiselect.min.css';

html, body, .x-form-item, .x-body, .x-form-field {
  font: 1rem/1.385 LatoWeb, "Helvetica Neue Medium", Helvetica, Arial, sans-serif;
  color: #333333;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  height: 100%;
}

#app {
  font: 13px/1.385 LatoWeb, "Helvetica Neue Medium", Helvetica, Arial, sans-serif;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: $gray-100;
}

.app {
  &-main {
    flex: 1;
    padding: 0.5rem 1rem 1rem;

    &.overlay {
      background-color: rgba(50, 50, 50, .5);
    }

    &.white-container {
      background-color: $white;
    }
  }
}

.rtl {
  direction: rtl;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity .5s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

.breadcrumb {
  background-color: var(--scheme-base-light1);
  color: var(--scheme-base-dark-accent);
  border-radius: 0 !important;
}

.breadcrumb li span a {
  font: unset;
  color: var(--scheme-base-dark-accent);
}

.breadcrumb-item + .breadcrumb-item::before {
  content: ">" !important;
  color: inherit !important;
}

.help-icon {
  height: 27px;
}

@media (max-width: 1100px) {
  .hideIfWidth1100 {
    display: none;
  }
}

.row {
  margin-left: 0 !important;
  margin-right: 0 !important;
  flex-wrap: unset;
}

.form-group.disabled label {
  color: #666;
  font-style: italic;
}

.redactor-modal-footer button[data-command='insert'],
.redactor-modal-footer button[data-command='update'] {
  float: right;
}

.cursor-pointer {
  cursor: pointer;
}

.redactor-styles a {
  text-decoration: underline;
}

.comments-portal {
  height: 0;
}

.picker-dialog-bg {
  z-index: 1060 !important;
}

.picker-dialog {
  z-index: 1070 !important;
}

@media print {
  #app, .app {
    background: transparent;
  }
}
</style>
